import React from 'react';
import { Tab as MuiTab, TabProps as MuiTabProps, tabClasses, styled, lighten, Stack } from '@mui/material';
import type { TabsProps } from '..';

export type TabProps = Omit<MuiTabProps, 'label'> & {
  label: string;
  styleVariant: TabsProps['styleVariant'];
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  to?: string;
};

const StyledTab = styled(MuiTab, {
  shouldForwardProp: (prop) => prop !== 'styleVariant',
})<Omit<TabProps, 'label'>>(({ theme, styleVariant }) => ({
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  color: theme.custom.colours?.grey500,
  margin: '1px',
  [`&.${tabClasses.root}`]: {
    marginRight: '12px',
    borderRadius:
      styleVariant === 'primary' || styleVariant === 'grey'
        ? '8px'
        : styleVariant === 'white-border'
        ? '6px'
        : undefined,
    padding:
      styleVariant === 'primary' || styleVariant === 'grey' || styleVariant === 'white-border' ? '8px 12px' : '12px',
  },
  [`&.${tabClasses.root}:hover`]: {
    backgroundColor: lighten(theme.palette.primary.main, 0.9),
    color: theme.palette.primary.main,
  },
  [`&.${tabClasses.selected}`]: {
    color:
      styleVariant === 'grey' || styleVariant === 'white-border'
        ? theme.custom.colours?.grey700
        : theme.palette.primary.main,
    backgroundColor:
      styleVariant === 'underline-filled' || styleVariant === 'primary'
        ? lighten(theme.palette.primary.main, 0.9)
        : styleVariant === 'grey'
        ? theme.custom.colours?.grey50
        : styleVariant === 'white-border'
        ? '#FFF'
        : undefined,
    boxShadow:
      styleVariant === 'white-border'
        ? '0px 1px 3px 0px rgba(16, 24, 40, 0.10), 0px 1px 2px 0px rgba(16, 24, 40, 0.06)'
        : undefined,
  },
}));

export const Tab: React.FC<TabProps> = ({ startAdornment, endAdornment, label, ...props }) => {
  return (
    <StyledTab
      {...props}
      label={
        <Stack direction="row" spacing={'8px'}>
          {startAdornment} <div>{label}</div> {endAdornment}
        </Stack>
      }
    />
  );
};
